import { createContext, useContext, useState, useEffect } from "react";
import { PropTypes } from "prop-types";
import getStockPrice from "../../api/stockPriceApi";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { variantMapper } from "../../../mappers/variant";
import { hasAssociatedLooks } from "../../components/looks/utils/hasAssociatedLooks";
import setLooksTracking from "../../analytics/pageLoad/looks";
import { isProductUnavailable } from "../../../utils/productUtils";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";

export const LooksProductsContext = createContext({});

const shouldMapLooksProducts = ({ isMixAndMatch, looksData, isUnavailable }) =>
  looksData?.looksProductsFromServer.length > 0 &&
  !isMixAndMatch &&
  !isUnavailable;

export const LooksProductsProvider = ({ children }) => {
  const { product, looksData, isMixAndMatch } =
    useRuntimeConstants();

  const isUnavailable = isProductUnavailable(product);

  const [looksProducts, setLooksProducts] = useState(null);
  const [shouldShowLooksSection, setShouldShowLooksSection] = useState(null);

  useEffect(() => {
    (async function mapLooksProducts() {
      if (
        shouldMapLooksProducts({
          isMixAndMatch,
          looksData,
          isUnavailable,
        })
      ) {
        const stockPriceProducts = await getStockPrice(product);
        const mappedLooksProducts = mapLooksProductsWithStockPrice({
          stockPriceProducts,
          looksData,
        });
        setLooksProducts(mappedLooksProducts);
        const showLooksCTA = hasAssociatedLooks({
          looksProducts: mappedLooksProducts,
        });
        setShouldShowLooksSection(showLooksCTA);
        setLooksTracking(showLooksCTA);
        return;
      }
      setShouldShowLooksSection(false);
      if (!isMixAndMatch) {
        setLooksTracking(false);
      }
    })();
  }, [product, looksData, isMixAndMatch, isUnavailable]);

  return (
    <LooksProductsContext.Provider
      value={{ looksProducts, shouldShowLooksSection }}
    >
      {children}
    </LooksProductsContext.Provider>
  );
};

const mapLooksProductsWithStockPrice = ({ stockPriceProducts, looksData }) => {
  const looksProducts = looksData.looksProductsFromServer.map(
    (looksProduct) => {
      let mappedLooksProduct;
      const stockPriceForProduct = stockPriceProducts.find(
        (stockPriceProduct) => stockPriceProduct.productId === looksProduct.id
      );

      if (stockPriceForProduct) {
        mappedLooksProduct = {
          ...looksProduct,
          hasMultipleColoursInStock:
            stockPriceForProduct.hasMultipleColoursInStock,
          hasMultiplePricesInStock:
            stockPriceForProduct.hasMultiplePricesInStock,
          isInStock: stockPriceForProduct.isInStock,
          price: stockPriceForProduct.productPrice,
          restockingSoonDetails: stockPriceForProduct.restockingSoonDetails || {
            shouldShowRestockingSoon: false,
          },
        };

        mappedLooksProduct.variants = mappedLooksProduct.variants
          ?.map((looksVariant) => {
            const stockPriceForVariant = stockPriceForProduct.variants?.find(
              (stockPriceVariant) =>
                stockPriceVariant.id === looksVariant.variantId
            );

            const hasVariantPrice =
              !!stockPriceForVariant?.price?.current?.value;
            if (!hasVariantPrice) {
              logError(ERROR_TYPE.StockPriceError, {
                message: "Variant missing stock price mapping",
                productId: looksProduct.id,
                variantId: looksVariant.variantId,
              });
              return null;
            }

            const stockPriceMapping = variantMapper(
              stockPriceForVariant,
              !!stockPriceForProduct.productPrice && hasVariantPrice
            );

            return {
              ...stockPriceMapping,
              ...looksVariant,
            };
          })
          ?.filter(Boolean);
      }

      return mappedLooksProduct;
    }
  );

  return filterOutProductsWithoutPrice(looksProducts.filter(Boolean));
};

const filterOutProductsWithoutPrice = (looksProducts) =>
  looksProducts.filter(
    (looksProduct) => looksProduct.isHero || looksProduct.price !== undefined
  );

export const useLooksProducts = () => useContext(LooksProductsContext);

LooksProductsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
